import React, { useMemo, useState } from 'react';
import { DollarSign, Plus, Trash2 } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import type { CreateRequisitionPayload, HiringReason, JobType } from '../api';
import { validateRequisicaoForm, type RequisicaoErrors } from '../validation/recrutamentoForms';
import { maskCurrencyBRL } from '../../../shared/utils/validators';
import { JOB_TYPE_LABELS, apiToCurrencyInput, currencyInputToApi } from './format';

export interface RequisicaoFormOption {
  id: string;
  label: string;
}

interface RequisicaoFormProps {
  companies: RequisicaoFormOption[];
  clients: RequisicaoFormOption[];
  costCenters: RequisicaoFormOption[];
  hiringReasons: HiringReason[];
  initialValues?: Partial<CreateRequisitionPayload>;
  loading?: boolean;
  submitLabel?: string;
  error?: string | null;
  onSubmit: (payload: CreateRequisitionPayload) => void;
  onSaveDraft?: (payload: CreateRequisitionPayload) => void;
  onCancel?: () => void;
}

const JOB_TYPES: JobType[] = ['clt', 'pj', 'estagio', 'temporario', 'aprendiz'];

/**
 * Formulário da requisição de vaga.
 *
 * As validações espelham as do servidor (campos obrigatórios, quantidade
 * ≥ 1, salário não negativo) e a regra condicional que o servidor tem
 * como dado: quando o motivo escolhido exige colaborador substituído, o
 * campo aparece e passa a ser obrigatório. Espelhar não é duplicar
 * autoridade — o servidor continua sendo quem recusa; aqui é para o
 * usuário não descobrir o erro depois de enviar.
 */
export function RequisicaoForm({
  companies,
  clients,
  costCenters,
  hiringReasons,
  initialValues,
  loading,
  submitLabel = 'Enviar para aprovação',
  error,
  onSubmit,
  onSaveDraft,
  onCancel,
}: RequisicaoFormProps) {
  /*
   * Os campos de dinheiro vivem mascarados no estado ("4.500,00") e são
   * convertidos para o formato da API ("4500.00") no envio. Guardar o
   * formato da API aqui obrigaria a mascarar a cada render e faria o
   * cursor pular enquanto o usuário digita.
   */
  const [values, setValues] = useState<Partial<CreateRequisitionPayload>>({
    vacanciesCount: 1,
    jobType: 'clt',
    ...initialValues,
    expectedSalary: apiToCurrencyInput(initialValues?.expectedSalary),
    benefits: (initialValues?.benefits ?? []).map((benefit) => ({
      ...benefit,
      estimatedValue: apiToCurrencyInput(benefit.estimatedValue),
    })),
  });
  const [errors, setErrors] = useState<RequisicaoErrors>({});

  const reason = useMemo(
    () => hiringReasons.find((item) => item.id === values.hiringReasonId),
    [hiringReasons, values.hiringReasonId],
  );
  const exigeSubstituido = reason?.requiresReplacedEmployee ?? false;

  function setField<K extends keyof CreateRequisitionPayload>(
    key: K,
    value: CreateRequisitionPayload[K],
  ) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: undefined }));
  }

  function validar(): CreateRequisitionPayload | null {
    // Valida já no formato que a API recebe — a máscara é só apresentação.
    const normalizados = {
      ...values,
      expectedSalary: currencyInputToApi(values.expectedSalary),
      benefits: (values.benefits ?? []).map((benefit) => ({
        ...benefit,
        estimatedValue: currencyInputToApi(benefit.estimatedValue),
      })),
    };

    // Regra em um lugar só: o mesmo módulo puro que os testes exercitam.
    const next = validateRequisicaoForm(normalizados, hiringReasons);
    setErrors(next);
    if (Object.values(next).some(Boolean)) return null;

    return {
      companyId: values.companyId as string,
      clientId: values.clientId as string,
      costCenterId: values.costCenterId as string,
      requestingArea: values.requestingArea!.trim(),
      positionTitle: values.positionTitle!.trim(),
      vacanciesCount: values.vacanciesCount as number,
      jobType: (values.jobType ?? 'clt') as JobType,
      hiringReasonId: values.hiringReasonId as string,
      replacedEmployeeName: exigeSubstituido
        ? values.replacedEmployeeName?.trim() || undefined
        : undefined,
      expectedSalary: normalizados.expectedSalary as string,
      justification: values.justification!.trim(),
      benefits: normalizados.benefits.filter((benefit) => benefit.description.trim()),
    };
  }

  const benefits = values.benefits ?? [];

  return (
    <form
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        const payload = validar();
        if (payload) onSubmit(payload);
      }}
    >
      <section className="grid gap-4 md:grid-cols-2">
        <AutocompleteSelect
          label="Empresa"
          options={companies.map((company) => ({ value: company.id, label: company.label }))}
          value={values.companyId ?? ''}
          onChange={(value) => setField('companyId', value)}
          error={errors.companyId}
        />

        <AutocompleteSelect
          label="Cliente"
          options={clients.map((client) => ({ value: client.id, label: client.label }))}
          value={values.clientId ?? ''}
          onChange={(value) => setField('clientId', value)}
          error={errors.clientId}
        />

        <AutocompleteSelect
          label="Centro de custo"
          options={costCenters.map((costCenter) => ({
            value: costCenter.id,
            label: costCenter.label,
          }))}
          value={values.costCenterId ?? ''}
          onChange={(value) => setField('costCenterId', value)}
          error={errors.costCenterId}
        />

        <TextInput
          label="Área solicitante"
          value={values.requestingArea ?? ''}
          onChange={(event) => setField('requestingArea', event.target.value)}
          error={errors.requestingArea}
        />

        <TextInput
          label="Cargo"
          value={values.positionTitle ?? ''}
          onChange={(event) => setField('positionTitle', event.target.value)}
          error={errors.positionTitle}
        />

        <TextInput
          label="Quantidade de vagas"
          type="number"
          min={1}
          value={values.vacanciesCount ?? 1}
          onChange={(event) => setField('vacanciesCount', Number(event.target.value))}
          error={errors.vacanciesCount}
        />

        <AutocompleteSelect
          label="Tipo da vaga"
          options={JOB_TYPES.map((type) => ({ value: type, label: JOB_TYPE_LABELS[type] }))}
          value={values.jobType ?? 'clt'}
          onChange={(value) => setField('jobType', (value || 'clt') as JobType)}
        />

        <AutocompleteSelect
          label="Motivo da contratação"
          options={hiringReasons.map((reason) => ({ value: reason.id, label: reason.label }))}
          value={values.hiringReasonId ?? ''}
          onChange={(value) => setField('hiringReasonId', value)}
          error={errors.hiringReasonId}
        />

        {/* Só aparece quando o motivo exige — a regra vem do catálogo. */}
        {exigeSubstituido && (
          <TextInput
            label="Colaborador substituído"
            value={values.replacedEmployeeName ?? ''}
            onChange={(event) => setField('replacedEmployeeName', event.target.value)}
            error={errors.replacedEmployeeName}
          />
        )}

        <TextInput
          label="Salário previsto"
          inputMode="numeric"
          icon={<DollarSign className="h-4 w-4" />}
          value={values.expectedSalary ?? ''}
          onChange={(event) => setField('expectedSalary', maskCurrencyBRL(event.target.value))}
          error={errors.expectedSalary}
        />
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700">Benefícios</h3>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() =>
              setField('benefits', [...benefits, { description: '', estimatedValue: '' }])
            }
          >
            Adicionar
          </Button>
        </div>
        {benefits.length === 0 && (
          <p className="text-sm text-gray-500">Nenhum benefício informado (opcional).</p>
        )}
        {errors.benefits && <p className="mb-2 text-xs text-red-600">{errors.benefits}</p>}
        <div className="space-y-2">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1">
                <TextInput
                  label="Descrição"
                  value={benefit.description}
                  onChange={(event) => {
                    const next = [...benefits];
                    next[index] = { ...benefit, description: event.target.value };
                    setField('benefits', next);
                  }}
                />
              </div>
              <div className="w-40">
                <TextInput
                  label="Valor estimado"
                  inputMode="numeric"
                  icon={<DollarSign className="h-4 w-4" />}
                  value={benefit.estimatedValue ?? ''}
                  onChange={(event) => {
                    const next = [...benefits];
                    next[index] = {
                      ...benefit,
                      estimatedValue: maskCurrencyBRL(event.target.value),
                    };
                    setField('benefits', next);
                  }}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Remover benefício"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setField('benefits', benefits.filter((_, i) => i !== index))}
              />
            </div>
          ))}
        </div>
      </section>

      <Textarea
        label="Justificativa"
        rows={4}
        value={values.justification ?? ''}
        onChange={(event) => setField('justification', event.target.value)}
        error={errors.justification}
      />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}
        {onSaveDraft && (
          <Button
            type="button"
            variant="secondary"
            disabled={loading}
            onClick={() => {
              const payload = validar();
              if (payload) onSaveDraft(payload);
            }}
          >
            Salvar rascunho
          </Button>
        )}
        <Button type="submit" loading={loading}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
